import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import Popover from 'react-text-selection-popover';
import Fade from '@material-ui/core/Fade';

import { useCodingQueueDetails } from '../../../../../modules/hooks/codingqueuedetail';
import EncounterCard from '../../../../../components/encountercard/components/encountercard';
import AddDxDialog from '../../../../../components/adddxdialog/components/adddxdialog';
import AddCommentToPatientDialog from '../../../../../components/addcommenttopatientdialog/addcommenttopatientdialog';
import HeaderInfoSection from '../../../../../components/headerinfosection/components/headerinfosection';
import CommentSection from '../../../../../components/commentsection/components/commentsection';
import WorkflowSection from '../../../../../components/workflowsection/components/workflowsection';
import CollapsableSection from '../../../../../components/collapsablesection/components/collapsablesection';
import LoadingDialog from '../../../../../components/loadingdialog/components/loadingdialog';
import DxSection from '../../../../../components/dxsection/components/dxsection';
import Lookback from '../../../../../components/lookback/components/lookback';
import PatientHistorySection from '../../../../../components/patienthistorysection/components/patienthistorysection';
import HoldSection from '../../../../../components/holdsection/holdsection';
import { GlobalHotKeys } from '../../../../../../node_modules/react-hotkeys';

const noOp = () => {};

const rootStyle = {
  height: '100%'
};

const leftColumnStyle = {
  height:'85%',
  overflowY:'hidden',
  width:'62%',
  display: 'inline-block',
  border: '1px solid #000'
};

const rightColumnStyle = {
  display: 'inline-block',
  width: '35%',
  verticalAlign:'top',
  position: 'relative',
  maxHeight: '85%',
  overflowY: 'scroll',
  border: '1px solid #000',
  overflowX: 'hidden'
};

const rightColumnSectionStyle = {
  display: 'inline-block',
  width: '100%'
};

const titleBarStyle = {
  color: '#fff',
  backgroundColor: '#303f9f',
  height: '33px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end'
};

const headerStyle = {
  color: '#fff',
  backgroundColor: '#303f9f',
  height: '33px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 0.35em'
};

const notesAreaRef = React.createRef();

const Details = ({
  service
}) => {
  const { encounterId } = useParams();
  const history = useHistory();

  const [state, boundActions] = useCodingQueueDetails(service, encounterId);

  const {
    details: {
      additionalInfo: { dateOfService },
      comments,
      currentEncounterInfo: {
        isOnHold,
        isFinalized,
        patientId,
        gotoEncounterDestination
      },
      dxList,
      holdData,
      encounterCard: { encounterText },
      encounterList,
      reasonOptions,
      patientHistory,
      encounterAuditReasons,
      userIsAuditor
    },
    addDxDialog,
    addCommentDialog,
    auditResolutionDialog,
    lookback
  } = state;

  return (
    <div style={rootStyle}>
      <div style={leftColumnStyle}>
        <div style={headerStyle}>
          <div>
            <label>
              {
                lookback.isVisible
                  ? 'Encounter List'
                  : 'Notes'
              }
            </label>
          </div>

          <div>
            <button
              onClick={() =>
                lookback.isVisible
                  ? boundActions.hideLookback()
                  : boundActions.showLookback()
              }
            >
              <span>
                {
                  lookback.isVisible ? 'Show Notes' : 'Show Encounter List'
                }
              </span>
            </button>
          </div>
        </div>

        <div>
          <Fade in={lookback.isVisible}>
            <div style={{ display: lookback.isVisible ? 'block' : 'none' }}>
              <Lookback
                encounterList={encounterList}
                dxFilterValue={lookback.dxFilterValue}
                hccFilterValue={lookback.hccFilterValue}
                handleLookbackFilterChange={boundActions.setLookbackFilterValue}
                redirectToReviewDetails={boundActions.redirectToReviewDetails}
              />
            </div>
          </Fade>

          {/* EncounterCard */}
          <Fade in={!lookback.isVisible}>
            <div style={{ display: !lookback.isVisible ? 'block' : 'none' }}>
              <EncounterCard
                noteMarkup={encounterText}
                forwardRef={notesAreaRef}
                dateOfService={dateOfService}
              />
            </div>
          </Fade>
        </div>
      </div>

      <div style={rightColumnStyle}>
        <div style={rightColumnSectionStyle}>
          <CollapsableSection title="Patient History">
            <PatientHistorySection historyData={patientHistory} />
          </CollapsableSection>
        </div>

        {
          holdData.holdData && holdData.holdData.length > 0
            ? (
              <div style={rightColumnSectionStyle}>
                <CollapsableSection title="Hold Data" shouldStartOpened={true}>
                  <HoldSection
                    holdData={holdData.holdData}
                    isReview={true}
                    openReviewDialog={noOp}
                  />
                </CollapsableSection>
              </div>
            )
            : null
        }

        <div style={rightColumnSectionStyle}>
          <CollapsableSection title="Dxes" shouldStartOpened={true}>
            <div>
              <DxSection
                encounterId={encounterId}
                listItems={dxList.listItems}
                openAddDxDialog={boundActions.openAddDxDialog}
                openAddReasonOptionDialog={boundActions.openAddReasonOptionDialog}
                unapproveDx={boundActions.unapproveDx}
                unremoveDx={boundActions.unremoveDx}
                isReview={true}
                encounterAuditReasons={encounterAuditReasons}
                toggleEncounterAuditReason={boundActions.toggleEncounterAuditReason}
                isMultiselect={dxList.isMultiselect}
                multiselectItems={dxList.multiselectItems}
                beginMultiselect={boundActions.beginMultiselect}
                endMultiselect={boundActions.endMultiselect}
                selectItemMultiselect={boundActions.selectItemMultiselect}
                deselectItemMultiselect={boundActions.deselectItemMultiselect}
                isAuditResolutionDialogVisible={auditResolutionDialog.isOpen}
                auditResolutionOptions={
                  auditResolutionDialog.auditResolutionOptions
                }
                openAuditResolutionDialog={noOp}
                closeAuditResolutionDialog={noOp}
                resolveAuditReason={noOp}
                selectedAuditReasonId={auditResolutionDialog.selectedAuditReasonId}
                selectedAuditReasonIsResolvedValue={
                  auditResolutionDialog.selectedAuditReasonIsResolvedValue
                }
                selectAuditResolutionOption={noOp}
                selectedAuditResolutionOptionId={
                  auditResolutionDialog.selectedAuditResolutionOptionId
                }
                hydrateEncounterAuditReasons={noOp}
              />
            </div>
          </CollapsableSection>
        </div>

        <CollapsableSection title="Comments">
          <div>
            <CommentSection
              comments={comments}
              openAddCommentDialog={boundActions.openAddCommentDialog}
              isReview={true}
              isAudit={false}
            />
          </div>
        </CollapsableSection>

        <CollapsableSection title="Additional Info">
          <div>
            <HeaderInfoSection
              encounterId={encounterId}
              {...state.details.additionalInfo}
            />
          </div>
        </CollapsableSection>
      </div>

      <WorkflowSection
        encounterId={encounterId}
        isOnHold={isOnHold}
        removeFromHold={boundActions.removeFromHold}
        placeOnHold={boundActions.placeOnHold}
        isFinalized={isFinalized}
        finalizeEncounter={boundActions.finalizeEncounter}
        unfinalizeEncounter={boundActions.unfinalizeEncounter}
        exitEncounter={boundActions.exitEncounter}
        doRedirect={history.push}
        isReview={true}
        allowReviewRedirect={gotoEncounterDestination !== ""}
        reviewDetailsRedirectUrl={() => {
          return gotoEncounterDestination === 'coding'
            ? service.serviceRoutes.goToCodingQueueDetails(encounterId)
            : service.serviceRoutes.goToAuditQueueDetails(encounterId);
        }}
        userIsAuditor={userIsAuditor}
        gotoEncounterDestination={gotoEncounterDestination}
        encounterAuditReasons={encounterAuditReasons}
      />

      <LoadingDialog
        isOpen={state.isLoading}
        loadingMessage={state.loadingMessage}
      />

      <AddDxDialog
        isOpen={addDxDialog.isOpen}
        dateOfService={dateOfService}
        encounterId={encounterId}
        supportingText={addDxDialog.supportingText}
        reasonOptions={reasonOptions || []}
        addDx={boundActions.addDx}
        closeDialog={boundActions.closeAddDxDialog}
      />

      <AddCommentToPatientDialog
        isOpen={addCommentDialog.isOpen}
        closeDialog={boundActions.closeAddCommentDialog}
        commentSourceType="Patient"
        commentSourceId={patientId}
        addComment={boundActions.addCommentToPatient}
        addCommentToPatient={noOp}
        addCommentToEncounter={noOp}
      />
    </div>
  );
}

Details.propTypes = {
  service: PropTypes.object.isRequired
};

export default Details;
